import { useState } from 'react';
import { Loader2 } from 'lucide-react';

const ROLES = [
  { value: 'attendant', label: 'Attendant', hint: 'Gate console: access, check-in, audits, move-ins, tickets' },
  { value: 'admin', label: 'Admin', hint: 'Everything an attendant can do, plus units, rates and delinquency' },
];

const BLANK = { email: '', first_name: '', last_name: '', phone: '', role: 'attendant' };

/** Create or edit a staff account. Role is only offered on create — changing
 *  someone's privileges is its own deliberate action, not a side effect of
 *  correcting a phone number. */
export default function StaffForm({ initial, mode, onSubmit, onCancel }) {
  const [form, setForm] = useState({ ...BLANK, ...initial });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [field, setField] = useState(null);

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError(null); setField(null);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err.message);
      setField(err.details?.field ?? null);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-900">
        {mode === 'create' ? 'New staff account' : 'Edit staff account'}
      </h2>

      {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="field-label mb-1" htmlFor="first_name">First name</label>
          <input id="first_name" required maxLength={80} className="field-input"
                 value={form.first_name} onChange={set('first_name')} />
        </div>
        <div>
          <label className="field-label mb-1" htmlFor="last_name">Last name</label>
          <input id="last_name" required maxLength={80} className="field-input"
                 value={form.last_name} onChange={set('last_name')} />
        </div>
        <div>
          <label className="field-label mb-1" htmlFor="email">Email (sign-in)</label>
          <input id="email" type="email" required maxLength={180} className="field-input"
                 value={form.email} onChange={set('email')}
                 aria-invalid={field === 'email' || undefined} />
        </div>
        <div>
          <label className="field-label mb-1" htmlFor="phone">Phone</label>
          <input id="phone" type="tel" maxLength={32} className="field-input"
                 value={form.phone ?? ''} onChange={set('phone')} />
        </div>
      </div>

      {mode === 'create' && (
        <fieldset className="mt-4">
          <legend className="field-label mb-1.5">Role</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {ROLES.map((r) => (
              <label key={r.value}
                     className={`flex cursor-pointer gap-2.5 rounded-lg border p-3 ${
                       form.role === r.value ? 'border-brand-600 bg-brand-50' : 'border-slate-300 hover:bg-slate-50'}`}>
                <input type="radio" name="role" value={r.value} className="mt-0.5 h-4 w-4 text-brand-600"
                       checked={form.role === r.value} onChange={set('role')} />
                <span>
                  <span className="block text-sm font-bold text-slate-900">{r.label}</span>
                  <span className="mt-0.5 block text-xs text-slate-600">{r.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Assign them to facilities after the account exists — an attendant with no assignment
            can sign in but has no gate to work.
          </p>
        </fieldset>
      )}

      <div className="mt-5 flex items-center gap-2">
        <button type="submit" disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {mode === 'create' ? 'Create account' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
      </div>
    </form>
  );
}
